/**
 * @fileoverview Hash-based Router
 * Minimal routing implementation using window.location.hash.
 * @module core/router
 *
 * CONTRACT:
 * - parseRouteFromLocation() parses the current hash into a route object
 * - listenToRouteChanges(callback) subscribes to route changes
 * - Routes are parsed from hash format: #/page or #/page/id or #/page/id/subpage
 * - No dependencies; pure DOM API usage
 *
 * ROUTE FORMATS:
 * - #/ or # or empty -> { page: "stationgate" }
 * - #/home -> { page: "home" }
 * - #/observatories -> { page: "observatories" }
 * - #/observatories/dollar_liquidity -> { page: "observatory", id: "dollar_liquidity" }
 * - #/observatories/dollar_liquidity/usd-core -> { page: "observatory", id: "dollar_liquidity", subPage: "usd-core" }
 * - #/observatories/dollar_liquidity/fx-stress -> { page: "observatory", id: "dollar_liquidity", subPage: "fx-stress" }
 * - #/observatories/dollar_liquidity/risk-credit -> { page: "observatory", id: "dollar_liquidity", subPage: "risk-credit" }
 * - #/observatories/dollar_liquidity/methodology -> { page: "observatory", id: "dollar_liquidity", subPage: "methodology" }
 * - #/signals -> { page: "signals" }
 * - #/charts?signals=X,Y -> { page: "charts" }
 * - #/dispatches -> { page: "dispatches" }
 * - #/forum -> { page: "forum" }
 * - #/manifesto -> { page: "manifesto" }
 * - #/about -> { page: "about" }
 */

/**
 * Route object representing the current navigation state.
 * @typedef {Object} Route
 * @property {string} page - The page identifier (e.g., "home", "observatories", "observatory")
 * @property {string} [id] - Optional resource identifier (e.g., observatory ID)
 * @property {string} [subPage] - Optional sub-page identifier (e.g., "usd-core", "fx-stress")
 */

/**
 * Callback function invoked when the route changes.
 * @callback RouteChangeCallback
 * @param {Route} route - The new route object
 */

/**
 * Valid page identifiers for the application.
 * @type {Set<string>}
 */
const VALID_PAGES = new Set([
  "home",
  "stationgate",
  "observatories",
  "observatory",
  "signals",
  "signals-in-action",
  "charts",
  "dispatches",
  "forum",
  "store",
  "manifesto",
  "about",
]);

/**
 * Valid sub-pages for observatories
 * @type {Set<string>}
 */
const VALID_OBSERVATORY_SUBPAGES = new Set([
  "overview",
  "usd-core",
  "fx-stress",
  "risk-credit",
  "methodology",
]);

/**
 * Parses the current window.location.hash into a route object.
 * Hash format: #/page or #/page/id or #/page/id/subpage
 *
 * @returns {Route} Parsed route object
 *
 * @example
 * // URL: http://example.com/#/observatories
 * parseRouteFromLocation(); // { page: "observatories" }
 *
 * // URL: http://example.com/#/observatories/dollar_liquidity
 * parseRouteFromLocation(); // { page: "observatory", id: "dollar_liquidity" }
 *
 * // URL: http://example.com/#/observatories/dollar_liquidity/usd-core
 * parseRouteFromLocation(); // { page: "observatory", id: "dollar_liquidity", subPage: "usd-core" }
 *
 * // URL: http://example.com/
 * parseRouteFromLocation(); // { page: "stationgate" }
 */
export function parseRouteFromLocation() {
  const hash = window.location.hash;

  // Handle empty hash or just "#" or "#/" -> stationgate (splash)
  if (!hash || hash === "#" || hash === "#/") {
    return { page: "stationgate" };
  }

  // Remove leading "#/" or "#"
  let path = hash.startsWith("#/") ? hash.slice(2) : hash.slice(1);
  
  // Strip query string (e.g., ?signals=X&range=Y) - it's handled by the page itself
  const queryIndex = path.indexOf('?');
  if (queryIndex !== -1) {
    path = path.slice(0, queryIndex);
  }

  // Split into segments
  const segments = path.split("/").filter((s) => s.length > 0);

  if (segments.length === 0) {
    return { page: "stationgate" };
  }

  const firstSegment = segments[0];

  // Special case: observatories with an ID (e.g., /observatories/dollar_liquidity)
  // May also have a sub-page (e.g., /observatories/dollar_liquidity/usd-core)
  if (firstSegment === "observatories" && segments.length >= 2) {
    const route = {
      page: "observatory",
      id: segments[1],
    };
    
    // Check for sub-page (third segment)
    if (segments.length >= 3) {
      const subPage = segments[2];
      if (VALID_OBSERVATORY_SUBPAGES.has(subPage)) {
        route.subPage = subPage;
      }
    }
    
    return route;
  }

  // Legacy support: /observatory/:id format still works
  if (firstSegment === "observatory" && segments.length >= 2) {
    const route = {
      page: "observatory",
      id: segments[1],
    };
    
    // Check for sub-page (third segment)
    if (segments.length >= 3) {
      const subPage = segments[2];
      if (VALID_OBSERVATORY_SUBPAGES.has(subPage)) {
        route.subPage = subPage;
      }
    }
    
    return route;
  }

  // All other pages are simple single-segment routes
  return { page: firstSegment };
}

/**
 * Subscribes to route changes via the hashchange event.
 * Immediately invokes the callback with the current route.
 *
 * @param {RouteChangeCallback} callback - Function to call when route changes
 * @returns {function(): void} Unsubscribe function to remove the listener
 *
 * @example
 * const unsubscribe = listenToRouteChanges((route) => {
 *   console.log("Route changed:", route);
 * });
 *
 * // Later, to stop listening:
 * unsubscribe();
 */
export function listenToRouteChanges(callback) {
  const handler = () => {
    const route = parseRouteFromLocation();
    callback(route);
  };

  // Subscribe to hash changes
  window.addEventListener("hashchange", handler);

  // Immediately invoke with current route
  handler();

  // Return unsubscribe function
  return () => {
    window.removeEventListener("hashchange", handler);
  };
}

/**
 * Navigates to a new route by updating the hash.
 * Does not trigger callback if hash is already the target.
 *
 * @param {string} path - Path to navigate to (e.g., "/observatories", "/observatories/dollar_liquidity")
 *
 * @example
 * navigateTo("/observatories");
 * navigateTo("/observatories/dollar_liquidity");
 * navigateTo("/observatories/dollar_liquidity/usd-core");
 */
export function navigateTo(path) {
  const targetHash = path.startsWith("/") ? `#${path}` : `#/${path}`;
  if (window.location.hash !== targetHash) {
    window.location.hash = targetHash;
  }
}

/**
 * Checks if a page identifier is valid.
 * @param {string} page - Page identifier to check
 * @returns {boolean} True if page is valid
 */
export function isValidPage(page) {
  return VALID_PAGES.has(page);
}

/**
 * Checks if a sub-page identifier is valid for observatories.
 * @param {string} subPage - Sub-page identifier to check
 * @returns {boolean} True if sub-page is valid
 */
export function isValidObservatorySubPage(subPage) {
  return VALID_OBSERVATORY_SUBPAGES.has(subPage);
}

export { VALID_PAGES, VALID_OBSERVATORY_SUBPAGES };
